import adminService from "./../AdminService";
import memeService from "./../MemeService";
import notificationService from "./../NotificationService";
import { useMemo, useCallback } from "react";
import useEntityState from "./../entityTemplateReducer";
import { useTranslation } from "react-i18next";

const PreloadReportedImageService = () => {
    const { t } = useTranslation("preloadErrors");

    const {
        getEntities,
        deleteEntityState,
        setEntityState,
        updateEntityState,
    } = useEntityState();

    const reportedImages = useMemo(() => {
        return getEntities("reportedImages");
    }, [getEntities]);

    const setReportedImages = useCallback(
        (image) => {
            if (!image)
                return console.warn(
                    "PreloadReportedImageService: setReportedImages missing image",
                );

            // Do not use this to update 1 template
            setEntityState(image, "reportedImages");
        },
        [setEntityState],
    );

    const replaceReportedImages = useCallback(
        (image) => {
            if (!image)
                return console.warn(
                    "PreloadReportedImageService: replaceReportedImages missing image",
                );
            updateEntityState(image, "reportedImages");
        },
        [updateEntityState],
    );

    const deleteReportedImageFromQueue = useCallback(
        async (image) => {
            if (!image || !image.templateId)
                return console.warn(
                    "PreloadReportedImageService: deleteReportedImageFromQueue missing image",
                );

            try {
                const response =
                    await adminService.deleteReportedUploadedImages(
                        "DELETE_FROM_REPORT",
                        image.templateId,
                    );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("reportedSomethingWentWrongDeletingFromQueue"),
                );
                console.error(
                    "PreloadReportedImageService: deleteReportedImageFromQueue ->",
                    error,
                );
                return false;
            }

            deleteEntityState(image, "reportedImages");

            return true;
        },
        [deleteEntityState, t],
    );

    const deleteReportedImageCompletly = useCallback(
        async (image) => {
            if (!image || !image.templateId)
                return console.warn(
                    "PreloadReportedImageService: deleteReportedImageCompletly missing image",
                );

            try {
                const response =
                    await adminService.deleteReportedUploadedImages(
                        "DELETE_COMPLETE",
                        image.templateId,
                    );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("reportedSomethingWhentWrongDeletingCompletly"),
                );
                console.error(
                    "PreloadReportedImageService: deleteReportedImageCompletly ->",
                    error,
                );
                return false;
            }

            deleteEntityState(image, "reportedImages");

            return true;
        },
        [deleteEntityState, t],
    );

    const clearReportedImageFromSearch = useCallback(
        async (image) => {
            if (!image || !image.templateId)
                return console.warn(
                    "PreloadReportedImageService: deleteReportedImageFromSearch missing image",
                );

            try {
                const response =
                    await adminService.deleteReportedUploadedImages(
                        "DELETE_FROM_SEARCH",
                        image.templateId,
                    );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("reportedSomethingWentWrongDeletingFromSearch"),
                );
                console.error(
                    "PreloadReportedImageService: deleteReportedImageFromSearch ->",
                    error,
                );
                return false;
            }

            image.searchable = "false";
            replaceReportedImages(image, "reportedImages");

            return true;
        },
        [replaceReportedImages, t],
    );

    const updateReportedImage = useCallback(
        async (image, reasonValue) => {
            if (!image || !image.templateId)
                return console.warn(
                    "PreloadReportedImageService: updateReportedImage missing image",
                );
            if (!reasonValue)
                return console.warn(
                    "PreloadReportedImageService: updateReportedImage missing reasonValue",
                );

            try {
                const response = await memeService.createOrUpdateReportImage(
                    image.templateId,
                    reasonValue,
                    image.uploader,
                    image.url,
                );
                if (!response.ok) throw new Error("Response was not ok");
            } catch (error) {
                notificationService.setMessage(
                    t("reportedSomethingWentWrongUpdating"),
                );
                console.error(
                    "PreloadReportedImageService: updateReportedImage ->",
                    error,
                );
                return false;
            }

            replaceReportedImages(image, "reportedImages");

            return true;
        },
        [replaceReportedImages, t],
    );

    const loadReportedImages = useCallback(async () => {
        return await loadImages(
            adminService.getAllReportedImages.bind(adminService),
            setReportedImages,
            true,
        ); // disabeling pagination temporarily
    }, [setReportedImages]);

    const loadImages = async (
        serviceFunction,
        setImageState,
        disablePagination,
    ) => {
        let reportedImageAccumulationMap = [];
        let paginationToken = null;

        try {
            // Start loading the images
            do {
                const responseObject = await serviceFunction(paginationToken);

                const responseObjectSelector = responseObject.reportedImages;

                const updatedImages = responseObjectSelector.map(
                    ({ templateId, url, ...rest }) => ({
                        ...rest,
                        title: templateId,
                        thumbnailUrl: url,
                        templateId,
                        url,
                    }),
                );

                // Accumulate images
                reportedImageAccumulationMap = [
                    ...reportedImageAccumulationMap,
                    ...updatedImages,
                ];

                // Update the state once with all the images
                setImageState(reportedImageAccumulationMap);

                // Set the next pagination token if exists if not disabled
                paginationToken = disablePagination
                    ? null
                    : responseObject.paginationToken;
            } while (paginationToken);
        } catch (error) {
            // In case of an error, notify the user and set the empty state
            notificationService.setMessage(t("reportedCouldNotLoad"));
            console.error(
                "PreloadReportedImageService: loadImages could not load reported images !",
                error,
            );
            setImageState(reportedImageAccumulationMap);
        }

        return reportedImageAccumulationMap;
    };

    return {
        loadReportedImages,
        clearReportedImageFromSearch,
        deleteReportedImageCompletly,
        deleteReportedImageFromQueue,
        replaceReportedImages,
        updateReportedImage,
        reportedImages,
    };
};

export default PreloadReportedImageService;
